// Design system tokens — "Sovereign Enterprise" direction
// Light-mode primary, warm off-white surface, navy chart-blue primary,
// copper for pursue/urgent states. Archivo Narrow for display, Geist for UI.
// Derived from the Stitch v2 design system but expressed in plain JS tokens
// so any styled-component can import what it needs without a theme provider.

export const colors = {
  // Surfaces — warm off-white base, layered containers
  surface: '#fcf9f8',
  surfaceContainer: '#f5f1ef',
  surfaceElevated: '#ffffff',
  surfaceInverse: '#1d2b3e',          // dark navy sidebar

  // Text on light
  onSurface: '#1c1b1b',
  onSurfaceMuted: '#6b6660',
  onSurfaceFaint: '#a09a93',

  // Text on dark (sidebar)
  onInverse: '#e6e8ec',
  onInverseMuted: '#94a3b8',
  onInverseFaint: '#64748b',

  // Brand
  primary: '#1d2b3e',                 // navy chart blue
  primaryHover: '#283849',
  primaryActive: '#15212f',
  accent: '#b8763d',                  // tactical copper — pursue, urgent
  accentHover: '#a4682f',
  accentMuted: '#e2c7a5',

  // Status semantics — desaturated for the defense-but-not-cosplay tone
  success: '#3f7d5c',
  successBg: '#e7efe9',
  warning: '#b8763d',                 // reuse copper
  warningBg: '#f7ece1',
  danger: '#9a3934',
  dangerBg: '#f5e3e1',
  info: '#3a5a7e',
  infoBg: '#e6ecf2',

  // Borders & dividers
  border: '#e5e0dc',
  borderStrong: '#c9c3bd',
  borderInverse: 'rgba(255,255,255,0.08)',
};

export const fonts = {
  display: '"Archivo Narrow", system-ui, -apple-system, sans-serif',
  body: '"Geist", -apple-system, BlinkMacSystemFont, "Inter", sans-serif',
  mono: '"Geist Mono", "SF Mono", ui-monospace, "Menlo", monospace',
};

export const text = {
  // Display — Archivo Narrow, condensed authority
  displayXl: { fontFamily: fonts.display, fontSize: '40px', fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em' },
  displayLg: { fontFamily: fonts.display, fontSize: '32px', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.01em' },
  displayMd: { fontFamily: fonts.display, fontSize: '24px', fontWeight: 500, lineHeight: 1.3 },

  // Body — Geist
  bodyLg: { fontFamily: fonts.body, fontSize: '17px', fontWeight: 400, lineHeight: 1.55 },
  bodyMd: { fontFamily: fonts.body, fontSize: '15px', fontWeight: 400, lineHeight: 1.5 },
  bodySm: { fontFamily: fonts.body, fontSize: '13px', fontWeight: 400, lineHeight: 1.5 },

  // Labels — Geist, uppercase, used for column headers, eyebrows
  labelMd: { fontFamily: fonts.body, fontSize: '12px', fontWeight: 600, lineHeight: 1, letterSpacing: '0.06em', textTransform: 'uppercase' },
  labelSm: { fontFamily: fonts.body, fontSize: '11px', fontWeight: 600, lineHeight: 1, letterSpacing: '0.04em', textTransform: 'uppercase' },
};

export const radius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  pill: '9999px',
};

export const space = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '40px',
  xxl: '64px',
};

export const shadow = {
  sm: '0 1px 2px rgba(20, 25, 35, 0.04), 0 1px 1px rgba(20, 25, 35, 0.03)',
  md: '0 2px 8px rgba(20, 25, 35, 0.06), 0 1px 2px rgba(20, 25, 35, 0.04)',
  lg: '0 12px 32px rgba(20, 25, 35, 0.10), 0 2px 6px rgba(20, 25, 35, 0.05)',
  focus: `0 0 0 3px ${colors.primary}22`,
};

const theme = { colors, fonts, text, radius, space, shadow };
export default theme;
